import { useAtom } from "jotai";
import { useCallback } from "react";
import { tagHandler } from "../handlers/tag-handler";
import {
  tagsAtom,
  activeWorkspaceTagsAtom,
  tagAutocompleteStateAtom,
  createTagAtom,
  updateTagAtom,
  deleteTagAtom,
  findOrCreateTagAtom,
  openTagAutocompleteAtom,
  closeTagAutocompleteAtom,
  updateTagAutocompleteQueryAtom,
  setTagAutocompleteResultsAtom,
  updateTagAutocompleteSelectionAtom,
  registerTagAtom,
  unregisterTagAtom,
  unregisterTagByIdAtom,
} from "../state/workspaces";
import type { Tag } from "@pineseed/gui/types";

/**
 * useTags - Bridge between tag handler and state atoms
 *
 * ARCHITECTURE COMPLIANCE:
 * - Pure UI hook - no business logic
 * - Connects handler to state via callbacks
 * - Only UI concerns and state access
 */

export function useTags() {
  const [tags] = useAtom(tagsAtom);
  const [activeWorkspaceTags] = useAtom(activeWorkspaceTagsAtom);
  const [autocompleteState] = useAtom(tagAutocompleteStateAtom);

  // Action atoms
  const [, createTag] = useAtom(createTagAtom);
  const [, updateTag] = useAtom(updateTagAtom);
  const [, deleteTag] = useAtom(deleteTagAtom);
  const [, findOrCreateTag] = useAtom(findOrCreateTagAtom);
  const [, openTagAutocomplete] = useAtom(openTagAutocompleteAtom);
  const [, closeTagAutocomplete] = useAtom(closeTagAutocompleteAtom);
  const [, updateTagAutocompleteQuery] = useAtom(
    updateTagAutocompleteQueryAtom,
  );
  const [, setTagAutocompleteResults] = useAtom(setTagAutocompleteResultsAtom);
  const [, updateTagAutocompleteSelection] = useAtom(
    updateTagAutocompleteSelectionAtom,
  );
  const [, registerTag] = useAtom(registerTagAtom);
  const [, unregisterTag] = useAtom(unregisterTagAtom);
  const [, unregisterTagById] = useAtom(unregisterTagByIdAtom);

  // Search function that uses handler
  const searchTags = useCallback(
    (query: string) => {
      return tagHandler.searchTags(activeWorkspaceTags, query);
    },
    [activeWorkspaceTags],
  );

  // Autocomplete functions
  const startAutocomplete = useCallback(
    (position: { x: number; y: number }, query: string = "") => {
      openTagAutocomplete(position, query);
      if (query.trim()) {
        const results = searchTags(query);
        setTagAutocompleteResults(results);
      }
    },
    [openTagAutocomplete, searchTags, setTagAutocompleteResults],
  );

  const stopAutocomplete = useCallback(() => {
    closeTagAutocomplete();
  }, [closeTagAutocomplete]);

  const updateAutocompleteQuery = useCallback(
    (query: string) => {
      updateTagAutocompleteQuery(query);
      const results = searchTags(query);
      setTagAutocompleteResults(results);
    },
    [updateTagAutocompleteQuery, searchTags, setTagAutocompleteResults],
  );

  const selectAutocompleteResult = useCallback(
    (direction: "up" | "down") => {
      updateTagAutocompleteSelection(direction);
    },
    [updateTagAutocompleteSelection],
  );

  const getCurrentSelection = useCallback(() => {
    if (
      autocompleteState.results.length === 0 ||
      autocompleteState.selectedIndex < 0
    ) {
      return null;
    }
    return autocompleteState.results[autocompleteState.selectedIndex];
  }, [autocompleteState]);

  // Tag creation and management
  const createNewTag = useCallback(
    (
      name: string,
      type: Tag["type"] = "general",
      options?: Partial<
        Pick<Tag, "color" | "icon" | "description" | "metadata">
      >,
    ) => {
      const validation = tagHandler.validateTagName(name);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }

      // State-level deduplication handles duplicate prevention
      return createTag({
        name: name.trim(),
        type,
        namespace: "general",
        ...options,
      });
    },
    [createTag],
  );

  const findOrCreateTagByName = useCallback(
    (name: string, type: Tag["type"] = "general") => {
      return findOrCreateTag(name, type);
    },
    [findOrCreateTag],
  );

  // Utility functions using handler
  const extractMentions = useCallback((text: string) => {
    return tagHandler.extractMentions(text);
  }, []);

  const resolveMentionsToTags = useCallback(
    (text: string) => {
      return tagHandler.resolveMentionsToTags(text, activeWorkspaceTags);
    },
    [activeWorkspaceTags],
  );

  const generateTagSuggestions = useCallback(
    (content: string) => {
      return tagHandler.generateTagSuggestions(content, activeWorkspaceTags);
    },
    [activeWorkspaceTags],
  );

  const validateTagName = useCallback((name: string) => {
    return tagHandler.validateTagName(name);
  }, []);

  const formatTagDisplay = useCallback((tag: Tag) => {
    return tagHandler.formatTagDisplay(tag);
  }, []);

  const parseTextWithTags = useCallback(
    (text: string) => {
      return tagHandler.parseTextWithTags(text, activeWorkspaceTags);
    },
    [activeWorkspaceTags],
  );

  const extractTagNameForDisplay = useCallback((content: string) => {
    return tagHandler.extractTagNameForDisplay(content);
  }, []);

  return {
    // State
    tags,
    activeWorkspaceTags,
    autocompleteState,

    // Search
    searchTags,

    // Autocomplete
    startAutocomplete,
    stopAutocomplete,
    updateAutocompleteQuery,
    selectAutocompleteResult,
    getCurrentSelection,

    // Tag management
    createNewTag,
    updateTag,
    deleteTag,
    findOrCreateTagByName,

    // Tag registration (for external systems)
    registerTag,
    unregisterTag,
    unregisterTagById,

    // Utility functions
    extractMentions,
    resolveMentionsToTags,
    generateTagSuggestions,
    validateTagName,
    formatTagDisplay,
    parseTextWithTags,
    extractTagNameForDisplay,
  };
}
